import {
  type GetAllParameterNamesResponse,
  type PcfParameterCandidate as ProtoCandidate,
  type PcfParameterReferenceDto,
} from './gen/pcf_mapping_service_pb';

class PcfParameterCatalogParseError extends Error {
  constructor(readonly field: string) {
    super(`Invalid PCF parameter catalogue field: ${field}`);
    this.name = 'PcfParameterCatalogParseError';
  }
}

export type PcfParameterReferenceIdentity = {
  readonly schemaVersion: number;
  readonly kind: string;
  readonly name: string;
  readonly scope: string;
  readonly sharedGuid: string;
  readonly builtInForgeTypeId: string;
  readonly builtInParameter: string | null;
  readonly documentKey: string;
  readonly ownerFamilyKey: string;
  readonly localParameterId: string | null;
};

export type PcfParameterCandidate = {
  readonly reference: PcfParameterReferenceIdentity;
  readonly label: string;
  readonly group: string;
  readonly dataType: string;
  readonly isProjectBinding: boolean;
  readonly categories: readonly string[];
};

export type PcfParameterCatalogResult = {
  readonly success: boolean;
  readonly parameterNames: readonly string[];
  readonly parameterCandidates: readonly PcfParameterCandidate[];
  readonly error?: string;
};

function adaptReference(reference: PcfParameterReferenceDto): PcfParameterReferenceIdentity {
  return {
    schemaVersion: reference.schemaVersion,
    kind: reference.kind,
    name: reference.name,
    scope: reference.scope,
    sharedGuid: reference.sharedGuid,
    builtInForgeTypeId: reference.builtInForgeTypeId,
    builtInParameter: reference.builtInParameter?.toString() ?? null,
    documentKey: reference.documentKey,
    ownerFamilyKey: reference.ownerFamilyKey,
    localParameterId: reference.localParameterId?.toString() ?? null,
  };
}

function adaptCandidate(candidate: ProtoCandidate): PcfParameterCandidate | null {
  if (!candidate.reference) {
    return null;
  }
  return {
    reference: adaptReference(candidate.reference),
    label: candidate.label,
    group: candidate.group,
    dataType: candidate.dataType,
    isProjectBinding: candidate.isProjectBinding,
    categories: candidate.categories,
  };
}

export function adaptPcfParameterCatalog(response: GetAllParameterNamesResponse): PcfParameterCatalogResult {
  const parameterCandidates = response.parameterCandidates.flatMap((candidate) => {
    const adapted = adaptCandidate(candidate);
    return adapted ? [adapted] : [];
  });
  return {
    success: response.isSuccess,
    parameterNames: response.parameterNames,
    parameterCandidates,
    ...(response.error ? { error: response.error } : {}),
  };
}

export function parsePcfParameterCatalogResultJson(resultJson: string): PcfParameterCatalogResult {
  const parsed: unknown = JSON.parse(resultJson);
  if (!isRecord(parsed)) throw new PcfParameterCatalogParseError('response');
  const names = parseStringArray(parsed.parameterNames, 'parameterNames');
  const rawCandidates = parsed.parameterCandidates ?? [];
  if (!Array.isArray(rawCandidates)) throw new PcfParameterCatalogParseError('parameterCandidates');
  const parameterCandidates = rawCandidates.map(parseJsonCandidate);
  const error = readString(parsed.error, 'error');
  return {
    success: readBoolean(parsed.isSuccess, 'isSuccess'),
    parameterNames: names,
    parameterCandidates,
    ...(error ? { error } : {}),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown, field: string): string {
  if (value === undefined) return '';
  if (typeof value !== 'string') throw new PcfParameterCatalogParseError(field);
  return value;
}

function readBoolean(value: unknown, field: string): boolean {
  if (value === undefined) return false;
  if (typeof value !== 'boolean') throw new PcfParameterCatalogParseError(field);
  return value;
}

function parseStringArray(value: unknown, field: string): readonly string[] {
  if (value === undefined) return [];
  if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) {
    throw new PcfParameterCatalogParseError(field);
  }
  return value;
}

function parseOptionalInt64(value: unknown, field: string): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' && /^-?\d+$/.test(value)) return BigInt(value).toString();
  if (typeof value === 'number' && Number.isSafeInteger(value)) return String(value);
  throw new PcfParameterCatalogParseError(field);
}

function parseJsonCandidate(value: unknown): PcfParameterCandidate {
  if (!isRecord(value) || !isRecord(value.reference)) {
    throw new PcfParameterCatalogParseError('parameterCandidates.reference');
  }
  const reference = value.reference;
  return {
    reference: {
      schemaVersion: readNumber(reference.schemaVersion, 'schemaVersion'),
      kind: readString(reference.kind, 'kind'),
      name: readString(reference.name, 'name'),
      scope: readString(reference.scope, 'scope'),
      sharedGuid: readString(reference.sharedGuid, 'sharedGuid'),
      builtInForgeTypeId: readString(reference.builtInForgeTypeId, 'builtInForgeTypeId'),
      builtInParameter: parseOptionalInt64(reference.builtInParameter, 'builtInParameter'),
      documentKey: readString(reference.documentKey, 'documentKey'),
      ownerFamilyKey: readString(reference.ownerFamilyKey, 'ownerFamilyKey'),
      localParameterId: parseOptionalInt64(reference.localParameterId, 'localParameterId'),
    },
    label: readString(value.label, 'label'),
    group: readString(value.group, 'group'),
    dataType: readString(value.dataType, 'dataType'),
    isProjectBinding: readBoolean(value.isProjectBinding, 'isProjectBinding'),
    categories: parseStringArray(value.categories, 'categories'),
  };
}

function readNumber(value: unknown, field: string): number {
  if (value === undefined) return 0;
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) {
    throw new PcfParameterCatalogParseError(field);
  }
  return value;
}

export function getPcfParameterReferenceIdentityKey(reference: PcfParameterReferenceIdentity): string {
  return JSON.stringify([
    reference.schemaVersion,
    reference.kind,
    reference.name,
    reference.scope,
    reference.sharedGuid,
    reference.builtInForgeTypeId,
    reference.builtInParameter,
    reference.documentKey,
    reference.ownerFamilyKey,
    reference.localParameterId,
  ]);
}
